import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { EntityFieldConfig } from '@/config/entities/types';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

interface DynamicFormProps {
  fields: EntityFieldConfig[];
  initialValues?: Record<string, any>;
  onSubmit: (data: Record<string, any>) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  submitLabel?: string;
}

// Build Zod schema from field configs
function buildSchema(fields: EntityFieldConfig[]) {
  const schemaObj: Record<string, z.ZodTypeAny> = {};
  
  fields.forEach(field => {
    let fieldSchema: z.ZodTypeAny;
    
    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
      case 'url':
        fieldSchema = field.required 
          ? z.string().min(1, `${field.label} is required`)
          : z.string().optional().or(z.literal(''));
        break;
      case 'textarea':
      case 'rich-text':
        fieldSchema = field.required
          ? z.string().min(1, `${field.label} is required`)
          : z.string().optional().or(z.literal(''));
        break;
      case 'number':
      case 'currency':
        fieldSchema = field.required
          ? z.number({ message: `${field.label} must be a number` })
          : z.number().optional().or(z.nan());
        break;
      case 'switch':
        fieldSchema = z.boolean();
        break;
      case 'select':
      case 'relation':
        fieldSchema = field.required
          ? z.string().min(1, `${field.label} is required`)
          : z.string().optional().or(z.literal(''));
        break;
      case 'multi-select':
        fieldSchema = field.required
          ? z.array(z.string()).min(1, `${field.label} is required`)
          : z.array(z.string()).optional();
        break;
      case 'date':
      case 'datetime':
        fieldSchema = field.required
          ? z.string().min(1, `${field.label} is required`)
          : z.string().optional().or(z.literal(''));
        break;
      default:
        fieldSchema = z.any();
    }
    
    // Use custom validation if provided
    if (field.validation) {
      fieldSchema = field.validation;
    }
    
    schemaObj[field.name] = fieldSchema;
  });
  
  return z.object(schemaObj);
}

// Get default values from field configs
function getDefaultValues(fields: EntityFieldConfig[], initialValues: Record<string, any> = {}) {
  const defaults: Record<string, any> = {};
  
  fields.forEach(field => {
    if (initialValues[field.name] !== undefined) {
      defaults[field.name] = initialValues[field.name];
    } else if (field.defaultValue !== undefined) {
      defaults[field.name] = field.defaultValue;
    } else {
      // Set appropriate default based on type
      switch (field.type) {
        case 'switch':
          defaults[field.name] = false;
          break;
        case 'multi-select':
          defaults[field.name] = [];
          break;
        case 'number':
        case 'currency':
          defaults[field.name] = undefined;
          break;
        default:
          defaults[field.name] = '';
      }
    }
  });
  
  return defaults;
}

export function DynamicForm({
  fields,
  initialValues = {},
  onSubmit,
  onCancel,
  loading = false,
  submitLabel = 'Save',
}: DynamicFormProps) {
  const [relationOptions, setRelationOptions] = useState<Record<string, Array<{ label: string; value: string }>>>({});
  const [submitting, setSubmitting] = useState(false);

  const schema = buildSchema(fields);
  const defaultValues = getDefaultValues(fields, initialValues);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues,
  });

  // Fetch relation options
  useEffect(() => {
    const fetchRelations = async () => {
      const relFields = fields.filter(f => f.type === 'relation' && f.relation);
      
      const optionsMap: Record<string, Array<{ label: string; value: string }>> = {};
      
      for (const field of relFields) {
        if (!field.relation) continue;
        
        let query = (supabase.from as any)(field.relation.table)
          .select(`${field.relation.valueField}, ${field.relation.labelField}`);
        
        if (field.relation.filter) {
          Object.entries(field.relation.filter).forEach(([key, value]) => {
            query = query.eq(key, value);
          });
        }
        
        const { data } = await query;
        
        optionsMap[field.name] = (data || []).map((item: any) => ({
          value: String(item[field.relation!.valueField]),
          label: String(item[field.relation!.labelField]),
        }));
      }
      
      setRelationOptions(optionsMap);
    };
    
    fetchRelations();
  }, [fields]);

  const handleSubmit = async (data: Record<string, any>) => {
    setSubmitting(true);
    try {
      // Clean up empty strings to null for optional fields
      const cleanedData = { ...data };
      fields.forEach(field => {
        if (!field.required && cleanedData[field.name] === '') {
          cleanedData[field.name] = null;
        }
      });
      
      await onSubmit(cleanedData);
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field: EntityFieldConfig) => {
    const watchedValues = form.watch();
    
    // Check showIf condition
    if (field.showIf && !field.showIf(watchedValues)) {
      return null;
    }

    // Check disabled condition
    const isDisabled = typeof field.disabled === 'function' 
      ? field.disabled(watchedValues) 
      : field.disabled;

    return (
      <FormField
        key={field.name}
        control={form.control}
        name={field.name}
        render={({ field: formField }) => (
          <FormItem className={field.gridSpan === 2 ? 'col-span-2' : ''}>
            <FormLabel>
              {field.label}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </FormLabel>
            <FormControl>
              {renderFieldInput(field, formField, isDisabled)}
            </FormControl>
            {field.helpText && (
              <FormDescription>{field.helpText}</FormDescription>
            )}
            <FormMessage />
          </FormItem>
        )}
      />
    );
  };

  const renderFieldInput = (field: EntityFieldConfig, formField: any, disabled?: boolean) => {
    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
      case 'url':
        return (
          <Input
            {...formField}
            type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : field.type === 'url' ? 'url' : 'text'}
            placeholder={field.placeholder}
            disabled={disabled || loading || submitting}
          />
        );
      
      case 'textarea':
        return (
          <Textarea
            {...formField}
            placeholder={field.placeholder}
            rows={4}
            disabled={disabled || loading || submitting}
          />
        );
      
      case 'number':
      case 'currency':
        return (
          <Input
            {...formField}
            type="number"
            placeholder={field.placeholder}
            disabled={disabled || loading || submitting}
            onChange={e => formField.onChange(e.target.value ? Number(e.target.value) : undefined)}
          />
        );
      
      case 'switch':
        return (
          <Switch
            checked={formField.value}
            onCheckedChange={formField.onChange}
            disabled={disabled || loading || submitting}
          />
        );
      
      case 'select':
        return (
          <Select
            value={formField.value || ''}
            onValueChange={formField.onChange}
            disabled={disabled || loading || submitting}
          >
            <SelectTrigger>
              <SelectValue placeholder={field.placeholder || `Select ${field.label}`} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case 'relation':
        return (
          <Select
            value={formField.value || ''}
            onValueChange={formField.onChange}
            disabled={disabled || loading || submitting}
          >
            <SelectTrigger>
              <SelectValue placeholder={field.placeholder || `Select ${field.label}`} />
            </SelectTrigger>
            <SelectContent>
              {relationOptions[field.name]?.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case 'date':
        return (
          <Input
            {...formField}
            type="date"
            disabled={disabled || loading || submitting}
          />
        );
      
      case 'datetime':
        return (
          <Input
            {...formField}
            type="datetime-local"
            disabled={disabled || loading || submitting}
          />
        );
      
      default:
        return (
          <Input
            {...formField}
            placeholder={field.placeholder}
            disabled={disabled || loading || submitting}
          />
        );
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          {fields.map(renderField)}
        </div>
        
        <div className="flex gap-2 pt-4">
          <Button type="submit" disabled={loading || submitting} className="flex-1">
            {(loading || submitting) ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              submitLabel
            )}
          </Button>
          <Button 
            type="button" 
            variant="outline" 
            onClick={onCancel}
            disabled={loading || submitting}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}
